import clsx from "clsx";
import { PlayerLoadingScreen } from "@/renderer/PlayerLoadingScreen";

import {
  LeaderboardSkeleton,
  MarketGridSkeleton,
  PackStageSkeleton,
  ProfileSkeleton,
  RacingDetailSkeleton,
  RoomSkeleton,
  StableSkeleton,
  TipsSkeleton,
  VerifySkeleton,
} from "./skeletonLayouts";
import {
  ActivitySkeleton,
  Fill,
  HorseRailSkeleton,
  RaceRailSkeleton,
  RowSkeleton,
  ScheduleSkeleton,
  SkeletonHeading,
  SkeletonSubnav,
  TableSkeleton,
} from "./skeletonPrimitives";

export type SkeletonVariant =
  | "rows"
  | "race-rail"
  | "horse-rail"
  | "schedule"
  | "tips"
  | "pack-stage"
  | "activity"
  | "table"
  | "profile"
  | "stable"
  | "market-grid"
  | "rooms"
  | "leaderboard"
  | "verify"
  | "racing-detail";

export function ContentSkeleton({
  rows = 3,
  variant = "rows",
}: {
  rows?: number;
  variant?: SkeletonVariant;
}) {
  if (variant === "race-rail") return <RaceRailSkeleton count={rows} />;
  if (variant === "horse-rail") return <HorseRailSkeleton count={rows} />;
  if (variant === "schedule") return <ScheduleSkeleton />;
  if (variant === "tips") return <TipsSkeleton />;
  if (variant === "pack-stage") return <PackStageSkeleton />;
  if (variant === "activity") return <ActivitySkeleton count={rows} />;
  if (variant === "table") return <TableSkeleton rows={rows} />;
  if (variant === "profile") return <ProfileSkeleton />;
  if (variant === "stable") return <StableSkeleton rows={rows} />;
  if (variant === "market-grid") return <MarketGridSkeleton />;
  if (variant === "rooms") return <RoomSkeleton rows={rows} />;
  if (variant === "leaderboard") return <LeaderboardSkeleton rows={rows} />;
  if (variant === "verify") return <VerifySkeleton rows={rows} />;
  if (variant === "racing-detail") return <RacingDetailSkeleton />;
  return <RowSkeleton rows={rows} />;
}

export function RouteSkeleton({ path }: { path: string }) {
  const route = routeName(path);
  return (
    <div
      aria-label={`Loading ${route}`}
      className={clsx(
        "page",
        "route-skeleton",
        `route-skeleton--${route}`,
        route === "home" && "page--home-showcase",
      )}
      role="status"
    >
      {route === "schedule" ? (
        <>
          <SkeletonHeading />
          <SkeletonSubnav />
          <RowSkeleton rows={6} />
        </>
      ) : null}
      {route === "home" ? (
        <>
          <SkeletonHeading />
          <div className="broadcast-layout">
            <section className="panel race-viewer">
              <div className="race-renderer-stack">
                <PlayerLoadingScreen />
              </div>
            </section>
            <aside className="market-panel">
              <SkeletonHeading />
              <SkeletonSubnav />
              <RowSkeleton rows={8} />
            </aside>
          </div>
          <SkeletonHeading />
          <RaceRailSkeleton count={6} />
        </>
      ) : null}
      {route === "packs" ? (
        <>
          <SkeletonSubnav />
          <section className="panel pack-opening-stage">
            <PackStageSkeleton />
          </section>
          <SkeletonHeading />
          <ActivitySkeleton count={4} />
          <SkeletonHeading />
          <section className="panel">
            <TableSkeleton rows={5} />
          </section>
        </>
      ) : null}
      {route === "player" ? (
        <>
          <ProfileSkeleton />
          <div className="skeleton-tabs">
            <Fill />
            <Fill />
            <Fill />
          </div>
          <StableSkeleton rows={5} />
        </>
      ) : null}
      {route === "marketplace" ? (
        <>
          <SkeletonSubnav />
          <SkeletonHeading />
          <MarketGridSkeleton />
          <SkeletonHeading />
          <ActivitySkeleton count={3} />
        </>
      ) : null}
      {route === "syndicates" ? (
        <>
          <SkeletonSubnav />
          <SkeletonHeading />
          <RoomSkeleton rows={1} form />
          <SkeletonHeading />
          <RoomSkeleton rows={3} />
        </>
      ) : null}
      {route === "leaderboard" ? <LeaderboardSkeleton rows={10} hero /> : null}
      {route === "verify" ? (
        <>
          <SkeletonHeading />
          <VerifySkeleton rows={6} />
        </>
      ) : null}
    </div>
  );
}

function routeName(path: string) {
  if (path.startsWith("/app/schedule")) return "schedule";
  if (path.startsWith("/app/packs")) return "packs";
  if (path.startsWith("/app/player")) return "player";
  if (path.startsWith("/app/marketplace")) return "marketplace";
  if (path.startsWith("/app/syndicates")) return "syndicates";
  if (path.startsWith("/app/leaderboards")) return "leaderboard";
  if (path.startsWith("/app/verify")) return "verify";
  return "home";
}
